import React, { useId, useMemo } from 'react'
import { BoltIcon, CpuChipIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import {
  NIE_WIEM, ETYKIETA_NIE_WIEM, OBJASNIENIA, MIEJSCA_LICZNIKA, POMIESZCZENIA, SCIANY, OPCJE_FALOWNIKA,
  OPCJE_MAGAZYNU, ODLEGLOSCI, FAZY, OPERATORZY, DROGI_KABLI, ZASIEG_WIFI,
  normalizujMiejsca, walidujMiejsca, polaAktywne, operatorZPpe,
} from '../../lib/teczka/miejsca'

/**
 * „Prosty audyt” (część `miejsca` teczki, `lib/teczka/miejsca.js`; pakiet P7 z 2026-10-24): 10 krótkich pytań
 * w trzech grupach, przy każdym „Nie wiem, wykonawca sprawdzi”, przy każdym miejscu opcjonalny opis słowami. Kod PPE
 * i operator w zwiniętej części „z rachunku” (opcjonalne). Bez zdjęć: to druga fala.
 *
 * Wygląd (uwagi przeglądu G7, 2026-10-25): grupy to karty z ikoną jak kafelki Rynku („KartaWykonawcy”,
 * „SprzetZlecenia”), odpowiedzi jak w ankiecie doboru wykonawców (`AnkietaDopasowania`): zaokrąglone przyciski,
 * wybrany niebieski, „nie wiem” przerywaną ramką.
 *
 * Stan trzyma rodzic (`miejsca`, `onZmiana`): zapis na koncie albo w przeglądarce to nie sprawa formularza.
 * `kontekst` z części `sprzet` albo z Projektu 3D (`zMagazynem: false` chowa magazyn); `podpowiedz` z `zProjektu`
 * (miejsce falownika z modelu, do potwierdzenia jednym kliknięciem).
 */
const WYBOR = 'min-h-[2.75rem] rounded-xl border px-4 py-2 text-sm font-medium text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1'
const POLE = 'w-full min-h-[2.75rem] rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400'

/** Jedno pytanie z odpowiedziami do kliknięcia; ostatnia to zawsze „nie wiem”. */
const Pytanie = ({ pytanie, opcje, wartosc, onWybor, podpowiedz, children }) => {
  const id = useId()
  return (
    <div>
      <p id={id} className="m-0 text-[15px] font-semibold leading-snug text-gray-900">{pytanie}</p>
      {podpowiedz && <p className="m-0 mt-1 text-[13px] leading-5 text-gray-600">{podpowiedz}</p>}
      {children}
      <div role="radiogroup" aria-labelledby={id} className="mt-3 flex flex-wrap gap-2">
        {opcje.map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={wartosc === k} onClick={() => onWybor(k)}
                  className={`${WYBOR} ${wartosc === k ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-gray-50'}`}>
            {l}
          </button>
        ))}
        <button type="button" role="radio" aria-checked={wartosc === NIE_WIEM} onClick={() => onWybor(NIE_WIEM)}
                className={`${WYBOR} border-dashed ${wartosc === NIE_WIEM ? 'border-amber-500 bg-white text-amber-700' : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'}`}>
          {ETYKIETA_NIE_WIEM}
        </button>
      </div>
    </div>
  )
}

/** Pole do wpisania z przełącznikiem „nie wiem” (moc przyłączeniowa, kod PPE). */
const Wpis = ({ etykieta, wartosc, onZmiana, podpowiedz, blad, zNieWiem = true, ...props }) => {
  const nieWiem = wartosc === NIE_WIEM
  const id = useId()
  return (
    <div>
      <label htmlFor={id} className="block text-[15px] font-semibold leading-snug text-gray-900">{etykieta}</label>
      {podpowiedz && <p className="m-0 mt-1 text-[13px] leading-5 text-gray-600">{podpowiedz}</p>}
      <input id={id} className={`${POLE} mt-2 ${blad ? 'border-red-400' : ''}`} value={nieWiem ? '' : wartosc} disabled={nieWiem}
             aria-invalid={Boolean(blad)} onChange={(e) => onZmiana(e.target.value)} {...props} />
      {blad && <p className="mt-1 text-xs text-red-600">{blad}</p>}
      {zNieWiem && (
        <label className="mt-2 flex min-h-[2.25rem] w-fit cursor-pointer items-center gap-2.5 text-sm text-gray-600">
          <input type="checkbox" className="h-5 w-5 rounded border-gray-300 accent-blue-600" checked={nieWiem}
                 onChange={(e) => onZmiana(e.target.checked ? NIE_WIEM : '')} />
          {ETYKIETA_NIE_WIEM}
        </label>
      )}
    </div>
  )
}

const Opis = ({ wartosc, onZmiana, przyklad, blad }) => (
  <label className="block text-sm">
    <span className="text-[13px] font-medium text-gray-700">Gdzie dokładnie? <span className="font-normal text-gray-500">(opcjonalnie, bez adresu i nazwisk)</span></span>
    <input className={`${POLE} mt-1.5 ${blad ? 'border-red-400' : ''}`} value={wartosc} maxLength={300} placeholder={przyklad}
           aria-invalid={Boolean(blad)} onChange={(e) => onZmiana(e.target.value)} />
    {blad && <span className="mt-1 block text-xs text-red-600">{blad}</span>}
  </label>
)

/** Znacznik stanu grupy: kolor niesie sama kropka, tekst zostaje szary, bez kolorowego tła. */
const STANY = {
  gotowe: ['bg-green-500', 'gotowe'],
  sprawdzi: ['bg-amber-400', 'wykonawca sprawdzi'],
  braki: ['bg-gray-300', 'do uzupełnienia'],
}
const Znacznik = ({ stan }) => (
  <span className="flex flex-none items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600">
    <span className={`h-2 w-2 rounded-full ${STANY[stan][0]}`} aria-hidden="true" />
    {STANY[stan][1]}
  </span>
)

const Grupa = ({ tytul, stan, Ikona, children }) => (
  <section aria-label={tytul} className="rounded-2xl border-[1.5px] border-gray-200 bg-white p-4 sm:p-5">
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gray-100 text-gray-700">
        <Ikona className="h-5 w-5" />
      </span>
      <h3 className="m-0 min-w-0 flex-1 text-base font-bold text-gray-900">{tytul}</h3>
      <Znacznik stan={stan} />
    </div>
    <div className="mt-5 space-y-6">{children}</div>
  </section>
)

/** Grupy formularza: które części teczki zbierają. */
const GRUPY = {
  prad: ['licznik', 'rozdzielnica'],
  falownik: ['falownik', 'magazyn', 'wifi'],
  siec: ['przylacze', 'kable'],
}

const PODPOWIEDZI_FALOWNIKA = {
  sciana: 'W Twoim projekcie 3D falownik wisi na zewnętrznej ścianie domu.',
  konstrukcja: 'W Twoim projekcie 3D falownik stoi przy konstrukcji na gruncie.',
}

const FormularzMiejsc = ({ miejsca, onZmiana, kontekst = {}, podpowiedz = null }) => {
  const m = useMemo(() => normalizujMiejsca(miejsca), [miejsca])
  const { bledy, odpowiedziane, wszystkich } = walidujMiejsca(m, kontekst)
  const aktywne = polaAktywne(m, kontekst)

  const ustaw = (czesc, zmiany) => onZmiana({ ...m, [czesc]: { ...m[czesc], ...zmiany } })
  const pole = (czesc, nazwaPola) => (v) => ustaw(czesc, { [nazwaPola]: v })
  // Kod PPE wskazuje operatora: podpowiadamy go, gdy klient jeszcze nie wybrał albo wybrał „nie wiem”.
  const ustawPpe = (v) => {
    const op = operatorZPpe(v)
    ustaw('przylacze', { ppe: v, ...(op && (!m.przylacze.operator || m.przylacze.operator === NIE_WIEM) ? { operator: op } : {}) })
  }

  const stan = (grupa) => {
    const czesci = GRUPY[grupa]
    const pola = aktywne.filter((p) => czesci.includes(p.czesc)).map((p) => m[p.czesc][p.pole])
    if (pola.some((v) => !v) || Object.keys(bledy).some((k) => czesci.includes(k.split('.')[0]))) return 'braki'
    return pola.includes(NIE_WIEM) ? 'sprawdzi' : 'gotowe'
  }
  const zMagazynem = aktywne.some((p) => p.czesc === 'magazyn')
  const operatorPpe = operatorZPpe(m.przylacze.ppe)
  const podpowiedzFalownika = !m.falownik.miejsce && podpowiedz?.falownik && PODPOWIEDZI_FALOWNIKA[podpowiedz.falownik]

  return (
    <div className="space-y-4">
      <p className="m-0 px-1 text-sm text-gray-600" aria-live="polite">
        Odpowiedzi: <strong className="tabular-nums text-gray-900">{odpowiedziane} z {wszystkich}</strong>.
        {' '}Czego nie wiesz, zaznacz „Nie wiem”: wykonawca sprawdzi to na miejscu.
      </p>

      <Grupa tytul="Licznik i rozdzielnica" stan={stan('prad')} Ikona={BoltIcon}>
        <Pytanie pytanie="Gdzie jest licznik prądu?" podpowiedz={OBJASNIENIA.licznik}
                 opcje={MIEJSCA_LICZNIKA} wartosc={m.licznik.gdzie} onWybor={pole('licznik', 'gdzie')} />
        <Opis wartosc={m.licznik.opis} onZmiana={pole('licznik', 'opis')} przyklad="np. skrzynka przy furtce" blad={bledy['licznik.opis']} />
        <Pytanie pytanie="Gdzie jest rozdzielnica z bezpiecznikami?" podpowiedz={OBJASNIENIA.rozdzielnica}
                 opcje={POMIESZCZENIA} wartosc={m.rozdzielnica.pomieszczenie} onWybor={pole('rozdzielnica', 'pomieszczenie')} />
        <Pytanie pytanie="Przy której ścianie domu wisi?" podpowiedz="Strona świata. Trafi na plan dla straży pożarnej."
                 opcje={SCIANY} wartosc={m.rozdzielnica.sciana} onWybor={pole('rozdzielnica', 'sciana')} />
        <Opis wartosc={m.rozdzielnica.opis} onZmiana={pole('rozdzielnica', 'opis')} przyklad="np. korytarz na parterze, przy drzwiach"
              blad={bledy['rozdzielnica.opis']} />
      </Grupa>

      <Grupa tytul="Falownik i magazyn" stan={stan('falownik')} Ikona={CpuChipIcon}>
        <Pytanie pytanie="Gdzie może wisieć falownik?" podpowiedz={OBJASNIENIA.falownik}
                 opcje={OPCJE_FALOWNIKA} wartosc={m.falownik.miejsce} onWybor={pole('falownik', 'miejsce')}>
          {podpowiedzFalownika && (
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700">
              {podpowiedzFalownika}
              <button type="button" onClick={() => ustaw('falownik', { miejsce: podpowiedz.falownik })}
                      className="font-semibold text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">
                Tak, jak w projekcie
              </button>
            </p>
          )}
        </Pytanie>
        <Pytanie pytanie="Jak daleko od rozdzielnicy?" podpowiedz={OBJASNIENIA.odleglosc}
                 opcje={ODLEGLOSCI} wartosc={m.falownik.odleglosc} onWybor={pole('falownik', 'odleglosc')} />
        <Opis wartosc={m.falownik.opis} onZmiana={pole('falownik', 'opis')} przyklad="np. garaż, ściana przy bramie" blad={bledy['falownik.opis']} />
        {zMagazynem && (
          <>
            <Pytanie pytanie="Gdzie może stać magazyn energii?" podpowiedz={OBJASNIENIA.magazyn}
                     opcje={OPCJE_MAGAZYNU} wartosc={m.magazyn.miejsce} onWybor={pole('magazyn', 'miejsce')} />
            <Opis wartosc={m.magazyn.opis} onZmiana={pole('magazyn', 'opis')} przyklad="np. piwnica, pod schodami" blad={bledy['magazyn.opis']} />
          </>
        )}
        <Pytanie pytanie="Czy przy falowniku łapie domowe Wi-Fi?" podpowiedz={OBJASNIENIA.wifi}
                 opcje={ZASIEG_WIFI} wartosc={m.wifi.zasieg} onWybor={pole('wifi', 'zasieg')} />
      </Grupa>

      <Grupa tytul="Przyłącze i kable" stan={stan('siec')} Ikona={WrenchScrewdriverIcon}>
        <Pytanie pytanie="Ile faz ma przyłącze?" podpowiedz={OBJASNIENIA.fazy}
                 opcje={FAZY} wartosc={m.przylacze.fazy} onWybor={pole('przylacze', 'fazy')} />
        <div className="max-w-xs">
          <Wpis etykieta="Moc przyłączeniowa, kW" inputMode="decimal" placeholder="np. 14" wartosc={m.przylacze.moc}
                onZmiana={pole('przylacze', 'moc')} podpowiedz={OBJASNIENIA.moc} blad={bledy['przylacze.moc']} />
        </div>
        <Pytanie pytanie="Którędy kable mogą zejść z dachu?" podpowiedz={OBJASNIENIA.kable}
                 opcje={DROGI_KABLI} wartosc={m.kable.droga} onWybor={pole('kable', 'droga')} />

        <details className="rounded-xl border border-gray-200 px-4 py-3" open={Boolean(m.przylacze.ppe || m.przylacze.operator) || undefined}>
          <summary className="cursor-pointer text-sm font-medium text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">
            Masz pod ręką rachunek za prąd? <span className="font-normal text-gray-500">(opcjonalnie, przyda się do formalności)</span>
          </summary>
          <div className="mt-4 space-y-5">
            <div className="max-w-sm">
              <Wpis etykieta="Kod PPE" inputMode="numeric" maxLength={24} placeholder="590…" wartosc={m.przylacze.ppe} zNieWiem={false}
                    onZmiana={ustawPpe} podpowiedz={OBJASNIENIA.ppe} blad={bledy['przylacze.ppe']} />
            </div>
            <div>
              <Pytanie pytanie="Operator sieci" podpowiedz={OBJASNIENIA.operator}
                       opcje={OPERATORZY} wartosc={m.przylacze.operator} onWybor={pole('przylacze', 'operator')} />
              {bledy['przylacze.operator']
                ? <p className="mt-1 text-xs text-red-600">{bledy['przylacze.operator']}</p>
                : operatorPpe && <p className="mt-1 text-xs text-gray-500">Rozpoznany po kodzie PPE.</p>}
            </div>
          </div>
        </details>
      </Grupa>
    </div>
  )
}

export default FormularzMiejsc
